// src/utils/cellRenderers.tsx - FIXED: Array typing issues
import React from 'react';
import { Building, Shield, Linkedin } from 'lucide-react';
import type { Contact } from '../types';
import { getInitials, getValidationStatus } from '../utils';

// Safe field access with fallback
const safeGet = (obj: Record<string, unknown>, key: string, fallback: string = '—'): string => {
  try {
    const value = obj?.[key];
    return value ? String(value).trim() : fallback;
  } catch {
    return fallback;
  }
};

export const renderContactColumn = (contact: Contact): React.ReactNode => {
  if (!contact || typeof contact !== 'object') {
    return <div className="contact-cell">Invalid contact data</div>;
  }

  const firstName = safeGet(contact, 'FIRST_NAME', '');
  const lastName = safeGet(contact, 'LAST_NAME', '');
  const fullName = `${firstName} ${lastName}`.trim() || 'Unknown Contact';
  const jobTitle = safeGet(contact, 'JOB_TITLE', '');
  const photoUrl = safeGet(contact, 'PHOTO_URL', '');

  return (
    <div className="contact-cell" style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 36 }}>
      {photoUrl && photoUrl !== '—' ? (
        <img
          src={photoUrl}
          alt={fullName}
          className="avatar-photo"
          style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', background: '#eee' }}
        />
      ) : (
        <div className="avatar" style={{ width: 32, height: 32, borderRadius: '50%', background: '#ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 14 }}>
          {getInitials(firstName, lastName)}
        </div>
      )}
      <div className="contact-info" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <span className="name" style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.1 }}>{fullName}</span>
        {jobTitle && (
          <span className="job-title" style={{ color: '#888', fontSize: 13, fontWeight: 400, lineHeight: 1.1 }}>{jobTitle}</span>
        )}
      </div>
    </div>
  );
};

export const renderPositionColumn = (contact: Contact): React.ReactNode => {
  if (!contact || typeof contact !== 'object') {
    return <div className="position-cell">—</div>;
  }

  const jobTitle = safeGet(contact, 'JOB_TITLE', '');
  const department = safeGet(contact, 'DEPARTMENT', '');
  const seniority = safeGet(contact, 'SENIORITY_LEVEL', '');
  
  return (
    <div className="position-cell">
      {jobTitle && jobTitle !== '—' && (
        <div className="job-title">
          <span>{jobTitle}</span>
        </div>
      )}
      {department && department !== '—' && (
        <div className="department">{department}</div>
      )}
      {seniority && seniority !== '—' && (
        <div className="seniority">{seniority}</div>
      )}
      {!jobTitle && !department && !seniority && <span>—</span>}
    </div>
  );
};

export const renderCompanyColumn = (contact: Contact): React.ReactNode => {
  if (!contact || typeof contact !== 'object') {
    return <div className="company-cell">—</div>;
  }

  const companyName = safeGet(contact, 'COMPANY_NAME', '');
  const companyDomain = safeGet(contact, 'COMPANY_DOMAIN', '');
  let companyLogoUrl = safeGet(contact, 'COMPANY_LOGO_URL', '');
  if (companyLogoUrl && !companyLogoUrl.startsWith('http')) {
    companyLogoUrl = 'https://' + companyLogoUrl;
  }

  return (
    <div className="company-cell" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 36 }}>
      <div style={{ display: 'flex', alignItems: 'center', height: 28 }}>
        {companyLogoUrl && companyLogoUrl !== '—' ? (
          <img
            src={companyLogoUrl}
            alt={companyName}
            className="company-logo"
            style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'contain', background: '#f5f5f5', border: '1px solid #eee', flexShrink: 0 }}
          />
        ) : (
          <div className="company-logo-placeholder" style={{ width: 28, height: 28, borderRadius: 6, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Building size={15} color="#bbb" />
          </div>
        )}
      </div>
      <div className="company-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', minWidth: 0 }}>
        <span className="company-name" style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{companyName || '-'}</span>
        {companyDomain && companyDomain !== '—' && (
          <a
            href={`https://${companyDomain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="company-domain"
            style={{ color: '#4f46e5', fontSize: 13, textDecoration: 'underline', fontWeight: 400, lineHeight: 1.1, paddingTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            onClick={e => e.stopPropagation()}
          >
            {companyDomain}
          </a>
        )}
      </div>
    </div>
  );
};

export const renderLocationColumn = (contact: Contact, type: 'personal' | 'company'): React.ReactNode => {
  if (!contact || typeof contact !== 'object') {
    return <div className="location-cell">—</div>;
  }

  // FIXED: Properly typed parts array
  const parts: string[] = [];
  
  if (type === 'personal') {
    const city = safeGet(contact, 'PERSONAL_CITY', '');
    const state = safeGet(contact, 'PERSONAL_STATE', '');
    const country = safeGet(contact, 'PERSONAL_COUNTRY', '');
    
    if (city && city !== '—') parts.push(city);
    if (state && state !== '—') parts.push(state);
    if (country && country !== '—') parts.push(country);
  } else {
    const city = safeGet(contact, 'COMPANY_CITY', '');
    const state = safeGet(contact, 'COMPANY_STATE', '');
    const country = safeGet(contact, 'COMPANY_COUNTRY', '');
    
    if (city && city !== '—') parts.push(city);
    if (state && state !== '—') parts.push(state);
    if (country && country !== '—') parts.push(country);
  }
  
  return (
    <div className="location-cell">
      <span className="location-text">
        {parts.length > 0 ? parts.join(', ') : '—'}
      </span>
    </div>
  );
};

export const renderEmailStatusColumn = (contact: Contact): React.ReactNode => {
  if (!contact || typeof contact !== 'object') {
    return <div className="status-indicator status-unverified">Unknown</div>;
  }

  const emailStatus = safeGet(contact, 'CURRENT_BUSINESS_EMAIL_VALIDATION_STATUS', '');
  const status = getValidationStatus(emailStatus);
  
  return (
    <div className={`status-indicator ${status.className}`}>
      <Shield size={12} />
      <span>{status.text}</span>
    </div>
  );
};

export const renderSimpleCell = (contact: Contact, fieldName: string): React.ReactNode => {
  if (!contact || typeof contact !== 'object') {
    return <div className="simple-cell">—</div>;
  }

  const value = safeGet(contact, fieldName, '');
  
  return (
    <div className="simple-cell">
      <span>{value || '—'}</span>
    </div>
  );
};

export const renderLinkedInCell = (contact: Contact): React.ReactNode => {
  if (!contact || typeof contact !== 'object') {
    return <div className="linkedin-cell">—</div>;
  }

  const linkedinUrl = safeGet(contact, 'LINKEDIN_URL', '');
  const linkedinStatus = safeGet(contact, 'LINKEDIN_URL_STATUS_CODE', '');
  
  // Don't show anything if no URL or if status is 404
  if (!linkedinUrl || linkedinUrl === '—' || linkedinStatus === '404') {
    return <div className="linkedin-cell">—</div>;
  }

  // Ensure the URL has a protocol
  let url = linkedinUrl;
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = 'https://' + url;
  }

  return (
    <div className="linkedin-cell">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          borderRadius: '4px',
          backgroundColor: '#0077b5',
          color: 'white',
          textDecoration: 'none',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#005885';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#0077b5';
        }}
        title="View LinkedIn Profile"
      >
        <Linkedin size={14} />
      </a>
    </div>
  );
};